import { useState, useEffect, useCallback } from 'react'
import {
  Calendar, CheckCircle, Circle, Flag, Pencil, Save, X,
  Tag, User, ListChecks, MessageCircle,
  Paperclip, FileText, Image as ImageIcon, Download, Check,
} from 'lucide-react'
import { clsx } from 'clsx'
import { Dialog, DialogHeader, DialogBody } from '@/components/ui/Dialog'
import { SubtaskList } from './SubtaskList'
import { CommentSection } from './CommentSection'
import { DatePicker } from '@/components/ui/DatePicker'
import { DatePickerBottomSheet } from '@/components/ui/DatePickerBottomSheet'
import { useSharedGroupStore } from '@/stores/sharedGroupStore'
import { useAuthStore } from '@/stores/authStore'
import {
  normalizeAssignees,
  type SharedTask, type SharedGroupMember, type GroupLabel,
  type TaskSubtask, type TaskComment,
} from '@/lib/sharedTypes'

interface SharedTaskDetailModalProps {
  isOpen: boolean
  onClose: () => void
  task: SharedTask
  groupId: string
  members: SharedGroupMember[]
  labels: GroupLabel[]
  canEdit: boolean
  panelMode?: boolean
  groupName?: string
  groupColor?: string
}

const PRIORITY_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  high: { label: '높음', color: 'text-red-500', icon: 'text-red-500' },
  medium: { label: '중간', color: 'text-amber-500', icon: 'text-amber-500' },
  low: { label: '낮음', color: 'text-blue-400', icon: 'text-blue-400' },
  none: { label: '없음', color: 'text-disabled', icon: 'text-disabled' },
}

const PRIORITY_OPTIONS: { value: string; label: string }[] = [
  { value: 'none', label: '없음' },
  { value: 'low', label: '낮음' },
  { value: 'medium', label: '중간' },
  { value: 'high', label: '높음' },
]

export function SharedTaskDetailModal({
  isOpen,
  onClose,
  task,
  groupId,
  members,
  labels,
  canEdit,
  panelMode,
  groupName,
  groupColor,
}: SharedTaskDetailModalProps) {
  const uid = useAuthStore((s) => s.user?.uid)
  const [subtasks, setSubtasks] = useState<TaskSubtask[]>([])
  const [comments, setComments] = useState<TaskComment[]>([])
  const [activeTab, setActiveTab] = useState<'subtasks' | 'comments'>('subtasks')

  // 편집 모드
  const [isEditing, setIsEditing] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editPriority, setEditPriority] = useState('')
  const [editDueDate, setEditDueDate] = useState('')
  const [editDueTime, setEditDueTime] = useState('')
  const [editAssigneeIds, setEditAssigneeIds] = useState<string[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches
  )

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 1023px)')
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  // 편집 모드 진입 시 현재 값으로 초기화
  const enterEditMode = () => {
    setEditTitle(task.title)
    setEditDescription(task.description || '')
    setEditPriority(task.priority || 'none')
    setEditDueDate(task.dueDate || '')
    setEditDueTime(task.dueTime || '')
    setEditAssigneeIds(normalizeAssignees(task).assigneeIds)
    setIsEditing(true)
  }

  const cancelEdit = () => {
    setIsEditing(false)
  }

  const saveEdit = async () => {
    const trimmedTitle = editTitle.trim()
    if (!trimmedTitle) return
    setIsSaving(true)
    try {
      const updates: Partial<SharedTask> = {}
      if (trimmedTitle !== task.title) updates.title = trimmedTitle
      if (editDescription.trim() !== (task.description || '')) updates.description = editDescription.trim()
      if (editPriority !== (task.priority || 'none')) updates.priority = editPriority as SharedTask['priority']
      if (editDueDate !== (task.dueDate || '')) updates.dueDate = editDueDate || undefined
      if (editDueTime !== (task.dueTime || '')) updates.dueTime = editDueTime || undefined
      const prevIds = normalizeAssignees(task).assigneeIds
      const idsChanged = editAssigneeIds.length !== prevIds.length || editAssigneeIds.some((id) => !prevIds.includes(id))
      if (idsChanged) {
        updates.assigneeIds = editAssigneeIds
        updates.assigneeNames = editAssigneeIds.map((id) => members.find((m) => m.uid === id)?.displayName || '').filter(Boolean)
        // 하위 호환
        updates.assigneeId = editAssigneeIds[0] || undefined
        updates.assigneeName = editAssigneeIds[0] ? members.find((m) => m.uid === editAssigneeIds[0])?.displayName || undefined : undefined
      }

      if (Object.keys(updates).length > 0) {
        updates.updatedAt = new Date().toISOString()
        await useSharedGroupStore.getState().updateTask(groupId, task.id, updates)
      }
      setIsEditing(false)
    } catch {
      // 편집 실패 — 모달은 유지
    } finally {
      setIsSaving(false)
    }
  }

  // Subscribe to subtasks and comments when modal opens
  useEffect(() => {
    if (!isOpen || !groupId || !task.id) return
    setIsEditing(false)

    let cancelled = false
    let unsubSubtasks: (() => void) | undefined
    let unsubComments: (() => void) | undefined

    const setup = async () => {
      const { getFirestoreUtils } = await import('@/lib/firebase')
      const { firestore, collection, onSnapshot } = await getFirestoreUtils()
      const { query, orderBy } = await import('firebase/firestore')

      if (cancelled) return

      const subtasksQuery = query(
        collection(firestore, `sharedGroups/${groupId}/tasks/${task.id}/subtasks`),
        orderBy('sortOrder')
      )
      unsubSubtasks = onSnapshot(subtasksQuery, (snapshot) => {
        if (cancelled) return
        const items: TaskSubtask[] = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as TaskSubtask[]
        setSubtasks(items)
      })

      const commentsQuery = query(
        collection(firestore, `sharedGroups/${groupId}/tasks/${task.id}/comments`),
        orderBy('createdAt')
      )
      unsubComments = onSnapshot(commentsQuery, (snapshot) => {
        if (cancelled) return
        const items: TaskComment[] = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as TaskComment[]
        setComments(items)
      })
    }

    setup()

    return () => {
      cancelled = true
      unsubSubtasks?.()
      unsubComments?.()
    }
  }, [isOpen, groupId, task.id])

  const store = useSharedGroupStore.getState

  const handleAddSubtask = useCallback(async (title: string) => {
    await store().addSubtask(groupId, task.id, title)
  }, [groupId, task.id])

  const handleToggleSubtask = useCallback(async (subtaskId: string, currentCompleted: boolean) => {
    await store().toggleSubtask(groupId, task.id, subtaskId, currentCompleted)
  }, [groupId, task.id])

  const handleDeleteSubtask = useCallback(async (subtaskId: string, wasCompleted: boolean) => {
    await store().deleteSubtask(groupId, task.id, subtaskId, wasCompleted)
  }, [groupId, task.id])

  const handleAddComment = useCallback(async (
    text: string,
    mentions?: string[],
    parentId?: string,
    image?: { url: string; width: number; height: number },
  ) => {
    await store().addComment(groupId, task.id, text, mentions, parentId, image)
  }, [groupId, task.id])

  const handleDeleteComment = useCallback(async (commentId: string) => {
    await store().deleteComment(groupId, task.id, commentId)
  }, [groupId, task.id])

  const handleReaction = useCallback(async (emoji: string) => {
    const user = useAuthStore.getState().user
    if (!user) return

    const reactions = { ...task.reactions }
    const current = reactions[emoji] || []

    if (current.includes(user.uid)) {
      reactions[emoji] = current.filter((id) => id !== user.uid)
      if (reactions[emoji].length === 0) delete reactions[emoji]
    } else {
      reactions[emoji] = [...current, user.uid]
    }

    await useSharedGroupStore.getState().updateTask(groupId, task.id, { reactions })
  }, [groupId, task.id, task.reactions])

  const isCompleted = task.status === 'completed'
  const priorityInfo = PRIORITY_CONFIG[task.priority]
  const taskLabels = (task.labels || [])
    .map((lid) => labels.find((l) => l.id === lid))
    .filter(Boolean) as GroupLabel[]

  const REACTION_EMOJIS = ['👍', '❤️', '🎉', '🔥', '👀', '💯']

  const content = (
        <div className="space-y-5">
          {/* 패널 모드: 그룹 배지 + 닫기 버튼 */}
          {panelMode && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {groupColor && (
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: groupColor }} />
                )}
                <span className="text-xs text-caption">{groupName || '공유 그룹'}</span>
              </div>
              <button onClick={onClose} className="p-1 rounded-lg hover-surface text-caption">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* 제목 */}
          <h3 className="text-base font-semibold text-heading">{isEditing ? '할 일 편집' : task.title}</h3>

          {/* ─── 편집 모드 ─── */}
          {isEditing ? (
            <div className="space-y-4">
              {/* 제목 */}
              <div>
                <label className="block text-xs font-medium text-caption mb-1.5">제목</label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  maxLength={200}
                  disabled={isSaving}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-strong bg-surface-secondary text-heading placeholder:text-disabled outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors disabled:opacity-50"
                />
              </div>

              {/* 메모 */}
              <div>
                <label className="block text-xs font-medium text-caption mb-1.5">메모</label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={3}
                  disabled={isSaving}
                  placeholder="메모를 입력하세요"
                  className="w-full px-3 py-2 text-sm rounded-lg border border-strong bg-surface-secondary text-heading placeholder:text-disabled outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors resize-none disabled:opacity-50"
                />
              </div>

              {/* 담당자 (복수 선택) */}
              <div>
                <label className="block text-xs font-medium text-caption mb-1.5">
                  담당자
                  {editAssigneeIds.length > 0 && (
                    <span className="ml-1 text-primary-500">{editAssigneeIds.length}명 선택</span>
                  )}
                </label>
                <div className="overflow-x-auto scrollbar-none -mx-1 px-1">
                  <div className="flex items-center gap-1.5 pb-1">
                    <button
                      type="button"
                      onClick={() => setEditAssigneeIds([])}
                      disabled={isSaving}
                      className={clsx(
                        'flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap',
                        editAssigneeIds.length === 0
                          ? 'bg-primary-500 text-white'
                          : 'bg-surface-tertiary text-body'
                      )}
                    >
                      미배정
                    </button>
                    {members.map((m) => {
                      const isSelected = editAssigneeIds.includes(m.uid)
                      return (
                        <button
                          key={m.uid}
                          type="button"
                          onClick={() => setEditAssigneeIds((prev) =>
                            prev.includes(m.uid) ? prev.filter((id) => id !== m.uid) : [...prev, m.uid]
                          )}
                          disabled={isSaving}
                          className={clsx(
                            'flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1.5',
                            isSelected
                              ? 'bg-primary-500 text-white'
                              : 'bg-surface-tertiary text-body'
                          )}
                        >
                          {isSelected ? (
                            <Check className="w-3 h-3 text-white" />
                          ) : (
                            <User className="w-3 h-3 text-caption" />
                          )}
                          {m.displayName}
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>

              {/* 마감일 */}
              <div>
                <label className="block text-xs font-medium text-caption mb-1.5">마감일</label>
                <div className="flex gap-3">
                  <div className="flex-1 px-3 py-2 rounded-lg border border-strong bg-surface-secondary">
                    {isMobile ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setShowDatePicker(true)}
                          disabled={isSaving}
                          className="flex items-center gap-2 text-sm text-heading disabled:opacity-50"
                        >
                          <span>
                            {editDueDate
                              ? new Intl.DateTimeFormat('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' }).format(new Date(editDueDate + 'T00:00'))
                              : '날짜를 선택하세요'}
                          </span>
                        </button>
                        <DatePickerBottomSheet
                          isOpen={showDatePicker}
                          onClose={() => setShowDatePicker(false)}
                          value={editDueDate || undefined}
                          onChange={setEditDueDate}
                        />
                      </>
                    ) : (
                      <DatePicker
                        value={editDueDate || undefined}
                        onChange={setEditDueDate}
                        placeholder="날짜를 선택하세요"
                      />
                    )}
                  </div>
                  {editDueDate && (
                    <div className="w-28">
                      <input
                        type="time"
                        value={editDueTime}
                        onChange={(e) => setEditDueTime(e.target.value)}
                        disabled={isSaving}
                        className="w-full px-3 py-2 rounded-lg border border-strong bg-surface-secondary text-heading text-sm outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors disabled:opacity-50"
                      />
                    </div>
                  )}
                </div>
                {editDueDate && (
                  <button
                    type="button"
                    onClick={() => { setEditDueDate(''); setEditDueTime('') }}
                    className="mt-1.5 text-xs text-caption hover:text-red-500 transition-colors"
                  >
                    날짜 제거
                  </button>
                )}
              </div>

              {/* 우선순위 */}
              <div>
                <label className="block text-xs font-medium text-caption mb-1.5">우선순위</label>
                <div className="flex gap-2">
                  {PRIORITY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setEditPriority(opt.value)}
                      disabled={isSaving}
                      className={clsx(
                        'flex-1 px-3 py-1.5 text-sm rounded-lg border transition-colors disabled:opacity-50',
                        editPriority === opt.value
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-medium'
                          : 'border-strong text-body hover-surface'
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* 편집 액션 */}
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-base">
                <button
                  onClick={cancelEdit}
                  disabled={isSaving}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-body hover-surface rounded-lg transition-colors disabled:opacity-50"
                >
                  <X className="w-4 h-4" />
                  취소
                </button>
                <button
                  onClick={saveEdit}
                  disabled={isSaving || !editTitle.trim()}
                  className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50 rounded-lg transition-colors"
                >
                  <Save className="w-4 h-4" />
                  {isSaving ? '저장 중...' : '저장'}
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* ─── 읽기 모드 ─── */}

              {/* Edit button + Status & Priority row */}
              <div className="flex items-center flex-wrap gap-2">
                {canEdit && (
                  <button
                    onClick={enterEditMode}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
                  >
                    <Pencil className="w-3 h-3" />
                    편집
                  </button>
                )}
                <span
                  className={clsx(
                    'inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium',
                    isCompleted
                      ? 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400'
                      : 'bg-surface-tertiary text-caption'
                  )}
                >
                  {isCompleted ? <CheckCircle className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                  {isCompleted ? '완료' : '진행 중'}
                </span>

                <span className={clsx('inline-flex items-center gap-1 text-xs font-medium', priorityInfo.color)}>
                  <Flag className={clsx('w-4 h-4', priorityInfo.icon)} />
                  {priorityInfo.label}
                </span>

                {(() => {
                  const { assigneeNames } = normalizeAssignees(task)
                  return assigneeNames.length > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs text-caption">
                      <User className="w-4 h-4" />
                      {assigneeNames.length > 2
                        ? `${assigneeNames[0]} 외 ${assigneeNames.length - 1}명`
                        : assigneeNames.join(', ')}
                    </span>
                  )
                })()}

                {task.dueDate && (
                  <span className="inline-flex items-center gap-1 text-xs text-caption">
                    <Calendar className="w-4 h-4" />
                    {task.dueDate}
                    {task.dueTime && ` ${task.dueTime}`}
                  </span>
                )}
              </div>

              {/* Labels */}
              {taskLabels.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Tag className="w-4 h-4 text-caption flex-shrink-0" />
                  {taskLabels.map((label) => (
                    <span
                      key={label.id}
                      className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                      style={{ backgroundColor: label.color }}
                    >
                      {label.name}
                    </span>
                  ))}
                </div>
              )}

              {/* Description */}
              {task.description && (
                <p className="text-sm text-body whitespace-pre-wrap">{task.description}</p>
              )}

              {/* Attachments */}
              {task.attachments && task.attachments.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-caption mb-2 flex items-center gap-1">
                    <Paperclip className="w-4 h-4" />
                    첨부파일 ({task.attachments.length})
                  </p>
                  <div className="space-y-1.5">
                    {task.attachments.map((att, i) => {
                      const isImage = att.type.startsWith('image/')
                      const Icon = isImage ? ImageIcon : FileText
                      const sizeStr = att.size < 1024 * 1024
                        ? `${(att.size / 1024).toFixed(1)}KB`
                        : `${(att.size / (1024 * 1024)).toFixed(1)}MB`
                      return (
                        <a
                          key={i}
                          href={att.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-secondary border border-base hover:bg-surface-tertiary transition-colors group"
                        >
                          {isImage ? (
                            <img src={att.url} alt={att.name} className="w-8 h-8 rounded object-cover flex-shrink-0" loading="lazy" />
                          ) : (
                            <Icon className="w-4 h-4 text-caption flex-shrink-0" />
                          )}
                          <span className="flex-1 text-sm text-heading truncate">{att.name}</span>
                          <span className="text-xs text-caption flex-shrink-0">{sizeStr}</span>
                          <Download className="w-4 h-4 text-caption opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                        </a>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Reactions */}
              <div className="flex items-center gap-1.5 flex-wrap">
                {Object.entries(task.reactions || {}).map(([emoji, uids]) => (
                  <button
                    key={emoji}
                    onClick={() => handleReaction(emoji)}
                    className={clsx(
                      'inline-flex items-center gap-1 px-2 py-1 rounded-lg border text-sm transition-colors',
                      uid && uids.includes(uid)
                        ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-base hover-surface'
                    )}
                  >
                    <span>{emoji}</span>
                    <span className="text-xs text-caption">{uids.length}</span>
                  </button>
                ))}

                {REACTION_EMOJIS.filter((e) => !(task.reactions || {})[e]).slice(0, 3).length > 0 && (
                  <div className="flex items-center gap-0.5">
                    {REACTION_EMOJIS.filter((e) => !(task.reactions || {})[e]).slice(0, 3).map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => handleReaction(emoji)}
                        className="p-1 rounded hover-surface text-sm opacity-40 hover:opacity-100 transition-opacity"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Tabs: Subtasks / Comments (항상 표시) */}
          <div className="border-t border-base pt-4">
            <div className="flex items-center gap-4 mb-4">
              <button
                onClick={() => setActiveTab('subtasks')}
                className={clsx(
                  'flex items-center gap-1.5 text-sm font-medium pb-1 border-b-2 transition-colors',
                  activeTab === 'subtasks'
                    ? 'border-primary-500 text-primary-500'
                    : 'border-transparent text-caption hover:text-heading'
                )}
              >
                <ListChecks className="w-4 h-4" />
                하위 항목
                {subtasks.length > 0 && (
                  <span className="text-xs text-caption">({subtasks.length})</span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('comments')}
                className={clsx(
                  'flex items-center gap-1.5 text-sm font-medium pb-1 border-b-2 transition-colors',
                  activeTab === 'comments'
                    ? 'border-primary-500 text-primary-500'
                    : 'border-transparent text-caption hover:text-heading'
                )}
              >
                <MessageCircle className="w-4 h-4" />
                댓글
                {comments.length > 0 && (
                  <span className="text-xs text-caption">({comments.length})</span>
                )}
              </button>
            </div>

            {activeTab === 'subtasks' ? (
              <SubtaskList
                subtasks={subtasks}
                canEdit={canEdit}
                onAdd={handleAddSubtask}
                onToggle={handleToggleSubtask}
                onDelete={handleDeleteSubtask}
              />
            ) : (
              <CommentSection
                groupId={groupId}
                taskId={task.id}
                comments={comments}
                onAdd={handleAddComment}
                onDelete={handleDeleteComment}
                members={members}
                currentUid={uid}
              />
            )}
          </div>
        </div>
  )

  if (panelMode) {
    return <div className="p-4">{content}</div>
  }

  return (
    <Dialog open={isOpen} onClose={onClose} size="lg">
      <DialogHeader
        title={isEditing ? '할 일 편집' : task.title}
        onClose={onClose}
      />
      <DialogBody>{content}</DialogBody>
    </Dialog>
  )
}
